import { DropoffSearchAddress } from "@redotech/merchant-sdk/rpc/schema/get-dropoff-locations";
import { PurchaseLabelType } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { OnboardingAverageProductWeightContext } from "@redotech/redo-merchant-app-common/team";
import { AddressForm } from "@redotech/redo-merchant-fulfillment-app/common/address-form";
import { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { getDatadogActionName } from "@redotech/redo-web/datadog-action-name";
import { memo, useContext, useMemo, useState } from "react";
import { getProductsForGroup } from "../select-shipments-modal/select-shipments-utils";
import { CreateLabelFormContent } from "./components/create-label-form-content";
import { DropoffLocationsContent } from "./components/dropoff-locations-content";
import { LabelConfirmation } from "./components/label-confirmation";
import {
  CreateLabelFormDef,
  CreateLabelValue,
} from "./create-label-modal-forms";
import {
  ConfirmationPageInfo,
  CreateLabelTarget,
  Page,
} from "./create-label-modal-types";
import {
  applyCustomShipFromAddress,
  applyCustomShipToAddress,
  getCreateLabelModalSubtitleByPage,
  getCreateLabelModalTitleByPage,
  getCreateLabelPrimaryButtonDisabledByPage,
  getCreateLabelPrimaryButtonTextByPage,
  getCreateLabelSecondaryButtonTextByPage,
  getDefaultCreateLabelValues,
  getProductsWeight,
  resolveCreateLabelTarget,
} from "./create-label-modal-utils";
import { usePurchaseReturnShippingFlow } from "./hooks/use-purchase-return-shipping-flow";
import { useReturnShippingRatesAndCost } from "./hooks/use-return-shipping-rates-and-cost";

interface ICreateLabelModalProps {
  "data-dd-action-name"?: string;
  onClose: () => void;
  return_: MerchantAppReturn;
  target: CreateLabelTarget;
  onLabelCreated?: (info: ConfirmationPageInfo) => void;
}

export const CreateLabelModal = memo(function CreateLabelModal({
  onClose,
  return_,
  target,
  onLabelCreated,
  "data-dd-action-name": actionName,
}: ICreateLabelModalProps) {
  const { t } = useTranslation();
  const onboardingData = useContext(OnboardingAverageProductWeightContext);
  const team = useContext(TeamContext);
  const { formatCurrency } = useContext(CurrencyContext);
  const [page, setPage] = useState(Page.CREATE_LABEL);

  const {
    labelType,
    shipmentId,
    shipmentGroupId,
    isAdditionalShipment,
    editingDropoffGroupId,
    isNewReShipment = false,
  } = resolveCreateLabelTarget(target);

  const isReShipment =
    isNewReShipment ||
    (labelType === PurchaseLabelType.Replace &&
      !!return_.reShipments?.some(
        (shipment) => shipment._shipment.id === shipmentId,
      ));

  const shipmentGroupIndex =
    shipmentGroupId && return_.shipmentGroups
      ? return_.shipmentGroups.findIndex((g) => g.id === shipmentGroupId)
      : -1;
  const shipmentNumber =
    shipmentGroupIndex >= 0 ? shipmentGroupIndex + 1 : undefined;

  const groupProducts = useMemo(
    () =>
      shipmentGroupId
        ? getProductsForGroup(return_, shipmentGroupId)
        : undefined,
    [return_, shipmentGroupId],
  );

  const [defaultValues] = useState<CreateLabelValue>(() =>
    getDefaultCreateLabelValues({
      return_,
      labelType,
      isNewReShipment,
      labelWeight: getProductsWeight({
        products: groupProducts ?? return_.products,
        team,
        onboardingData,
      }),
      shipmentId,
      // Editing a drop-off carries no shipmentId; resolve its ship-to from the
      // drop-off's own group so it keeps the return address (and its routing id).
      shipmentGroupId: shipmentGroupId ?? editingDropoffGroupId,
    }),
  );
  const createLabelInput = useInput(CreateLabelFormDef, defaultValues);

  const {
    fromAddress,
    toAddress,
    labelRatesQuery,
    rates,
    labelRateInUse,
    originalLabelCost,
    merchantCost,
    dropoffOptions,
    dropoffEnabledForTeam,
    dropoffOptionsLoading,
    selectedDropoff,
    dropoffPriceLoading,
    optionLabelType,
    optionOriginalLabelCost,
    isFirstLabel,
    isFirstLabelOrReplacingFirstLabel,
    redoMaxLabelPrice,
  } = useReturnShippingRatesAndCost({
    team,
    return_,
    createLabelInput,
    ratesEnabled: page === Page.CREATE_LABEL,
    isReShipment,
    shipmentId,
    labelType,
    editingDropoffGroupId,
  });

  const { confirmationPageInfo, submit, isPending } =
    usePurchaseReturnShippingFlow({
      setPage,
      onShippingCreated: onLabelCreated,
    });

  // Search-center for the drop-off map: the customer ship-from address. Provider
  // routing travels on merchantAddress (the ship-to) per getDropoffLocations.
  const dropoffCustomerAddress = useMemo<
    DropoffSearchAddress | undefined
  >(() => {
    if (!selectedDropoff) {
      return undefined;
    }
    const ship = return_.shipping_address;
    if (!ship?.address1 || !ship.city) {
      return undefined;
    }
    return {
      name: ship.name,
      address1: ship.address1,
      address2: ship.address2,
      city: ship.city,
      province: ship.province,
      zip: ship.zip,
      country: ship.country,
      latitude: ship.latitude,
      longitude: ship.longitude,
    };
  }, [selectedDropoff, return_.shipping_address]);

  const handlePrimaryClick = useHandler(() => {
    switch (page) {
      case Page.CREATE_LABEL: {
        if (!toAddress) {
          throw new Error(t("No ship-to address selected"));
        }
        if (!fromAddress) {
          throw new Error(t("No ship-from address selected"));
        }
        if (!team) {
          return;
        }

        if (selectedDropoff) {
          submit({
            method: "dropoff",
            props: {
              teamId: team._id,
              returnId: return_.id,
              dropoffId: editingDropoffGroupId,
              merchantAddress: toAddress,
              provider: selectedDropoff.provider,
              labelType,
              shipmentId,
              originalLabelCost,
            },
          });
          break;
        }

        if (!labelRateInUse) {
          return;
        }

        submit({
          method: "label",
          props: {
            teamId: team._id,
            returnId: return_.id,
            rate: labelRateInUse,
            weight: {
              value: createLabelInput.inputs.labelWeight.value.weight,
              unit: createLabelInput.inputs.labelWeight.value.unit,
            },
            shipmentId,
            shipmentGroupId,
            toAddress,
            fromAddress,
            labelType,
            originalLabelCost,
            replacedDropoffGroupId: editingDropoffGroupId,
            isAdditionalShipment,
            isReShipment: isNewReShipment,
          },
        });
        break;
      }
      case Page.CUSTOM_SHIP_FROM:
        applyCustomShipFromAddress(createLabelInput, setPage, isReShipment);
        break;
      case Page.CUSTOM_SHIP_TO:
        applyCustomShipToAddress(createLabelInput, setPage, isReShipment);
        break;
      case Page.DROPOFF_LOCATIONS:
        setPage(Page.CREATE_LABEL);
        break;
      case Page.CONFIRMATION:
        if (!confirmationPageInfo?.success) {
          void labelRatesQuery.refetch();
          setPage(Page.CREATE_LABEL);
        } else if (
          confirmationPageInfo.kind === "dropoff" &&
          confirmationPageInfo.qrCodeUrl
        ) {
          window.open(confirmationPageInfo.qrCodeUrl, "_blank");
        } else if (
          confirmationPageInfo.kind === "label" &&
          confirmationPageInfo.labelUrl
        ) {
          window.open(confirmationPageInfo.labelUrl, "_blank");
        } else {
          onClose();
        }
        break;
    }
  });

  const handleModalCloseRequested = useHandler(() => {
    if (page === Page.DROPOFF_LOCATIONS) {
      setPage(Page.CREATE_LABEL);
      return;
    }
    onClose();
  });

  const handleSecondaryClick = useHandler(() => {
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (page) {
      case Page.CREATE_LABEL:
        onClose();
        break;
      case Page.CUSTOM_SHIP_FROM:
      case Page.CUSTOM_SHIP_TO:
        setPage(Page.CREATE_LABEL);
        break;
      case Page.CONFIRMATION:
        if (confirmationPageInfo?.success && confirmationPageInfo.qrCodeUrl) {
          window.open(confirmationPageInfo.qrCodeUrl, "_blank");
        }
        break;
    }
  });

  const modalTitle = useMemo(() => {
    if (editingDropoffGroupId && page === Page.CREATE_LABEL) {
      return t("Edit drop-off");
    }
    return getCreateLabelModalTitleByPage(
      page,
      labelType,
      confirmationPageInfo,
      t,
    );
  }, [editingDropoffGroupId, page, labelType, confirmationPageInfo, t]);

  const modalSubtitle = useMemo(() => {
    if (editingDropoffGroupId && page === Page.CREATE_LABEL) {
      return t("Update the drop-off for this return.");
    }
    return getCreateLabelModalSubtitleByPage(page, labelType, t);
  }, [editingDropoffGroupId, page, labelType, t]);

  const primaryButtonText = getCreateLabelPrimaryButtonTextByPage(
    page,
    labelRateInUse || selectedDropoff
      ? formatCurrency(merchantCost)
      : undefined,
    confirmationPageInfo,
    t,
  );

  const secondaryButtonText = getCreateLabelSecondaryButtonTextByPage(
    page,
    confirmationPageInfo,
    t,
  );

  if (!team) {
    return null;
  }

  return (
    <RedoModal
      data-dd-action-name={getDatadogActionName(
        actionName,
        "create_label_close",
      )}
      onModalCloseRequested={handleModalCloseRequested}
      primaryButton={{
        "data-dd-action-name":
          page === Page.CREATE_LABEL
            ? selectedDropoff
              ? getDatadogActionName(actionName, "create_dropoff_confirm")
              : getDatadogActionName(actionName, "create_label_confirm")
            : undefined,
        disabled: getCreateLabelPrimaryButtonDisabledByPage(
          page,
          // A label needs carrier rates; a drop-off replacing a label needs them
          // for the credit. Other drop-offs don't.
          labelRatesQuery.isFetching &&
            (!selectedDropoff || dropoffPriceLoading),
          !!labelRateInUse || !!selectedDropoff,
        ),
        onClick: handlePrimaryClick,
        text: primaryButtonText,
        size: "sm",
        pending: isPending,
      }}
      secondaryButton={
        secondaryButtonText
          ? {
              "data-dd-action-name":
                page === Page.CREATE_LABEL
                  ? getDatadogActionName(actionName, "create_label_cancel")
                  : undefined,
              onClick: handleSecondaryClick,
              text: secondaryButtonText,
              size: "sm",
            }
          : undefined
      }
      subtitle={modalSubtitle}
      title={modalTitle}
    >
      {page === Page.CREATE_LABEL && (
        <CreateLabelFormContent
          createLabelInput={createLabelInput}
          data-dd-action-name={actionName}
          dropoffEnabledForTeam={dropoffEnabledForTeam}
          dropoffOptions={dropoffOptions}
          dropoffPriceLoading={dropoffPriceLoading || dropoffOptionsLoading}
          groupProducts={groupProducts}
          isFirstLabel={isFirstLabel}
          isFirstLabelOrReplacingFirstLabel={isFirstLabelOrReplacingFirstLabel}
          isPending={isPending}
          isReShipment={isReShipment}
          labelRateInUse={labelRateInUse}
          labelRatesLoading={labelRatesQuery.isFetching}
          optionLabelType={optionLabelType}
          optionOriginalLabelCost={optionOriginalLabelCost}
          rates={rates}
          redoMaxLabelPrice={redoMaxLabelPrice}
          return_={return_}
          setPage={setPage}
          shipmentId={shipmentId}
          shipmentNumber={shipmentNumber}
        />
      )}
      {page === Page.CUSTOM_SHIP_FROM && (
        <AddressForm
          form={createLabelInput.inputs.customShipFromAddressForm}
          title=""
        />
      )}
      {page === Page.CUSTOM_SHIP_TO && (
        <AddressForm
          form={createLabelInput.inputs.customShipToAddressForm}
          title=""
        />
      )}
      {page === Page.CONFIRMATION && (
        <LabelConfirmation confirmationPageInfo={confirmationPageInfo} />
      )}
      {page === Page.DROPOFF_LOCATIONS &&
        selectedDropoff &&
        dropoffCustomerAddress &&
        toAddress && (
          <DropoffLocationsContent
            customerAddress={dropoffCustomerAddress}
            customerCountry={return_.shipping_address?.country ?? undefined}
            merchantAddress={toAddress}
            provider={selectedDropoff.provider}
            teamId={team._id}
          />
        )}
    </RedoModal>
  );
});
